import React, { useState } from 'react';
import Pagination from './Pagination';
//formatters
import { formatDate } from '../../shared/utils/formatters';

// Column definition interface
export interface Column<T> {
  header: string;
  accessor: keyof T | ((item: T) => React.ReactNode);
  sortable?: boolean;
  className?: string;
  cell?: (item: T) => React.ReactNode;
}

// DataTable props interface
interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
  };
  onSort?: (field: string, direction: 'ASC' | 'DESC') => void;
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
  actions?: (item: T) => React.ReactNode;
  isLoading?: boolean;
  emptyMessage?: string;
  errorMessage?: string | null;
  className?: string;
  /**
   * Optional custom card renderer for the mobile (<md) layout. When omitted, a
   * default label/value card is generated from `columns` + `actions`.
   */
  renderCard?: (item: T) => React.ReactNode;
}

/**
 * DataTable Component
 * 
 * A reusable data table component with sorting and pagination
 */
function DataTable<T>({
  columns,
  data,
  keyField,
  pagination,
  onSort,
  sortField: externalSortField,
  sortDirection: externalSortDirection,
  actions,
  isLoading = false,
  emptyMessage = 'No data available',
  errorMessage = null,
  className = '',
  renderCard,
}: DataTableProps<T>) {
  const [internalSortField, setInternalSortField] = useState<string | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  
  // Use external sort state if provided, otherwise use internal state
  const sortField = externalSortField || internalSortField;
  const sortDirection = externalSortDirection || internalSortDirection;

  // Handle column header click for sorting
  const handleSort = (column: Column<T>) => {
    if (!column.sortable || typeof column.accessor !== 'string' || !onSort) {
      return;
    }

    const newDirection = sortField === column.accessor && sortDirection === 'ASC' ? 'DESC' : 'ASC';
    setInternalSortField(column.accessor as string);
    setInternalSortDirection(newDirection);
    onSort(column.accessor as string, newDirection);
  };

  // Render sort indicator
  const renderSortIndicator = (column: Column<T>) => {
    if (!column.sortable || typeof column.accessor !== 'string') {
      return null;
    }

    if (sortField !== column.accessor) {
      return (
        <svg className="w-3 h-3 ml-1 opacity-30" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }

    if (sortDirection === 'ASC') {
      return (
        <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
      );
    } else {
      return (
        <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  // Render cell content
  const renderCell = (item: T, column: Column<T>) => {
    // Use custom cell renderer if provided
    if (column.cell) {
      return column.cell(item);
    }
    
    if (typeof column.accessor === 'function') {
      return column.accessor(item);
    }

    const value = item[column.accessor];
    
    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // Handle date values
    if (value instanceof Date) {
      return formatDate(value, 'medium');
    }
    
    return value as React.ReactNode;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-w-full overflow-hidden rounded-lg shadow">
        <div className="bg-white p-8 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (errorMessage) {
    return (
      <div className="min-w-full overflow-hidden rounded-lg shadow">
        <div className="bg-white p-8 text-center text-red-500">
          {errorMessage}
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className="min-w-full overflow-hidden rounded-lg shadow">
        <div className="bg-white p-8 text-center text-gray-500">
          {emptyMessage}
        </div>
      </div>
    );
  }

  // Default mobile card: label/value pairs from the columns plus the actions row.
  const renderDefaultCard = (item: T) => (
    <dl className="space-y-2">
      {columns.map((column, index) => (
        <div key={index} className="flex justify-between gap-3">
          <dt className="text-sm font-medium text-gray-500">{column.header}</dt>
          <dd className="text-sm text-gray-900 text-right">{renderCell(item, column)}</dd>
        </div>
      ))}
      {actions && (
        <div className="pt-2 mt-1 border-t border-gray-100 flex flex-wrap gap-2 justify-end">
          {actions(item)}
        </div>
      )}
    </dl>
  );

  return (
    <div className={`overflow-hidden shadow ring-1 ring-black ring-opacity-5 ${className}`}>
      {/* Desktop: horizontal-scrolling table (unchanged from md up) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  scope="col"
                  className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 ${
                    column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  } ${column.className || ''}`}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center">
                    {column.header}
                    {column.sortable && renderSortIndicator(column)}
                  </div>
                </th>
              ))}
              {actions && <th scope="col" className="relative px-3 py-3.5 text-sm font-semibold text-gray-900">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((item) => (
              <tr key={item[keyField] as React.Key} className="hover:bg-gray-50">
                {columns.map((column, cellIndex) => (
                  <td key={cellIndex} className={`whitespace-nowrap px-3 py-4 text-sm text-gray-500 ${column.className || ''}`}>                     
                    {renderCell(item, column)}
                  </td>
                ))}
                {actions && (
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                    {actions(item)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked card list (hidden from md up) */}
      <div className="md:hidden divide-y divide-gray-200 bg-white">
        {data.map((item) => (
          <div key={item[keyField] as React.Key} className="p-4">
            {renderCard ? renderCard(item) : renderDefaultCard(item)}
          </div>
        ))}
      </div>

      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={pagination.onPageChange}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalItems}
        />
      )}
    </div>
  );
}

export default DataTable; 